import {CampaignType} from 'common/src/modules/campaigns/campaigns.types';
import {OptimizeByName, OptimizeByValue} from 'common/src/modules/optimization/optimization.types';

export type OptimizeByOption = {
  name: OptimizeByName;
  value: OptimizeByValue;
  uiName: string;
  title: string;
  description: string | null;
};

const OPTIMIZE_BY_OPTIONS: OptimizeByOption[] = [
  {
    name: OptimizeByName.Clicks,
    value: OptimizeByValue.Clicks,
    uiName: 'Clicks',
    title: 'Optimize By Clicks (CTR)',
    description: null,
  },
  {
    name: OptimizeByName.Conversions,
    value: OptimizeByValue.Conversions,
    uiName: 'Conversions',
    title: 'Optimize by Website Conversion Rate',
    description:
      "The KPI is the ratio of ('click through conversions'+'view through conversions')/'viewable impressions'. 'Conversion' is considered as any event on website, like 'purchase' or 'registration' which sent as a conversion after user viewed or clicked on an ad during the lookback period",
  },
  {
    name: OptimizeByName.ClickThroughConversions,
    value: OptimizeByValue.ClickThroughConversions,
    uiName: 'Click Through Conversions',
    title: 'Optimize by Website Click Through Conversion Rate',
    description:
      "The KPI is the ratio of 'click through conversions'/'viewable impressions'. 'Click Through Conversion' is considered as any event on website, like 'purchase' or 'registration' which sent as a conversion after user clicked on an ad during the lookback period",
  },
];

/**
 * Get available optimize by options based on campaign type.
 * Replicates the UI filtering logic from CampaignOptimizationComponent.tsx
 */
function getAvailableOptimizeByOptions(campaignType: CampaignType): OptimizeByOption[] {
  const isDisplayCampaign = [
    CampaignType.DynamicBanners,
    CampaignType.InBannerVideoAds,
    CampaignType.NativeAds,
  ].includes(campaignType);

  // For display campaigns: all options with 'creative' and 'display' tags
  // Based on the enum factory, all three options (Clicks, Conversions, ClickThroughConversions)
  // have both 'creative' and 'display' tags, so return all
  if (isDisplayCampaign) {
    return OPTIMIZE_BY_OPTIONS;
  }

  // For Facebook campaigns: options with 'facebook' tag
  // Clicks and Conversions have 'facebook' tag, but ClickThroughConversions doesn't
  if (campaignType === CampaignType.FacebookVideoAds) {
    return OPTIMIZE_BY_OPTIONS.filter((opt) =>
      [OptimizeByValue.Clicks, OptimizeByValue.Conversions].includes(opt.value),
    );
  }

  // For TikTok campaigns: options with 'tiktok' tag
  // Clicks and Conversions have 'tiktok' tag, but ClickThroughConversions doesn't
  if (campaignType === CampaignType.TikTokAds) {
    return OPTIMIZE_BY_OPTIONS.filter((opt) =>
      [OptimizeByValue.Clicks, OptimizeByValue.Conversions].includes(opt.value),
    );
  }

  // For YouTube campaigns: options with 'google' tag
  // Clicks and Conversions have 'google' tag, but ClickThroughConversions doesn't
  if (campaignType === CampaignType.YoutubeVideoAds) {
    return OPTIMIZE_BY_OPTIONS.filter((opt) =>
      [OptimizeByValue.Clicks, OptimizeByValue.Conversions].includes(opt.value),
    );
  }

  // Default: options with 'creative' tag (all three have it)
  return OPTIMIZE_BY_OPTIONS;
}

export default getAvailableOptimizeByOptions;
